import { CreditCard, DollarSign, Download, Edit, LogOut, MoreHorizontal, Printer, Receipt, Smartphone } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { Input } from '../components/common/Input';
import Footer from '../components/layout/Footer';
import ordersService from '../services/ordersService';
import { theme } from '../styles/theme';
import { formatBillData, generatePDF, printBill } from '../utils/printUtils';

const Container = styled.div`
  min-height: 100vh;
  background-color: ${theme.colors.background};
  padding: ${theme.spacing.lg};
  padding-bottom: 80px;
  position: relative;

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.sm};
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.xl};
  padding: ${theme.spacing.lg};
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.md};
  flex-wrap: wrap;
  gap: ${theme.spacing.md};

  h1 {
    font-size: ${theme.typography.fontSize['2xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.text};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.md};
    h1 {
      font-size: ${theme.typography.fontSize.xl};
    }
  }
`;

const LogoutButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: ${theme.spacing.lg};

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const OrdersList = styled.div`
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.md};
  max-height: calc(100vh - 200px);
  overflow-y: auto;
`;

const OrdersHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.border};

  h2 {
    font-size: ${theme.typography.fontSize.xl};
    font-weight: ${theme.typography.fontWeight.semiBold};
    color: ${theme.colors.text};
  }
`;

const OrderCard = styled(Card)`
  margin-bottom: ${theme.spacing.md};
  cursor: pointer;
  border: 3px solid ${({ selected }) => selected ? theme.colors.primary : 'transparent'};
  background-color: ${({ selected }) => selected ? `${theme.colors.primary}10` : theme.colors.white};

  &:hover {
    border-color: ${theme.colors.primary};
  }
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.md};

  div {
    h3 {
      font-size: ${theme.typography.fontSize.lg};
      font-weight: ${theme.typography.fontWeight.semiBold};
      color: ${theme.colors.text};
      margin-bottom: ${theme.spacing.xs};
    }

    p {
      font-size: ${theme.typography.fontSize.sm};
      color: ${theme.colors.textSecondary};
    }
  }

  span {
    font-size: ${theme.typography.fontSize.xl};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.primary};
  }
`;

const OrderItems = styled.div`
  margin-top: ${theme.spacing.md};
  padding-top: ${theme.spacing.md};
  border-top: 1px solid ${theme.colors.border};
`;

const OrderItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.sm};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text};

  &:last-child {
    margin-bottom: 0;
  }
`;

const BillPanel = styled.div`
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.md};
  position: sticky;
  top: ${theme.spacing.lg};
  max-height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
`;

const BillHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.border};

  h2 {
    font-size: ${theme.typography.fontSize.xl};
    font-weight: ${theme.typography.fontWeight.semiBold};
    color: ${theme.colors.text};
  }
`;

const BillDetails = styled.div`
  flex: 1;
  margin-bottom: ${theme.spacing.lg};
  overflow-y: auto;
  padding-bottom: ${theme.spacing.lg};

  /* Ensure smooth scrolling on mobile */
  -webkit-overflow-scrolling: touch;
`;

const BillRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.md};
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.text};

  ${({ highlight }) => highlight && `
    font-size: ${theme.typography.fontSize.xl};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.primary};
    margin-top: ${theme.spacing.md};
    padding-top: ${theme.spacing.md};
    border-top: 2px solid ${theme.colors.border};
  `}
`;

const DiscountSection = styled.div`
  margin-bottom: ${theme.spacing.lg};
  padding: ${theme.spacing.md};
  background-color: ${theme.colors.secondary};
  border-radius: ${theme.borderRadius.md};
`;

const DiscountInput = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.sm};
`;

const PaymentButtons = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(64px, 1fr));
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const PaymentButton = styled(Button)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.sm};
  font-size: ${theme.typography.fontSize.sm};
  min-height: 44px;

  svg {
    width: 18px;
    height: 18px;
  }

  span {
    font-size: ${theme.typography.fontSize.sm};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.xs};
  }
`;

const ActionsContainer = styled.div`
  position: sticky;
  bottom: 0;
  background: ${theme.colors.white};
  padding-top: ${theme.spacing.sm};
  padding-bottom: ${theme.spacing.sm};
  border-top: 1px solid ${theme.colors.border};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  z-index: ${theme.zIndex.modal};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
`;

const ActionButton = styled.button`
  flex: 1;
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  border: 2px solid ${theme.colors.border};
  background-color: ${theme.colors.white};
  color: ${theme.colors.text};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: ${theme.typography.fontFamily};
  font-weight: ${theme.typography.fontWeight.semiBold};

  &:hover {
    background-color: ${theme.colors.secondary};
    border-color: ${theme.colors.primary};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing['3xl']};
  color: ${theme.colors.textSecondary};
`;

// Orders are loaded inside the CounterBilling component (hooks & fetch placed inside the component)

function CounterBilling() {
  const navigate = useNavigate();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [discountInput, setDiscountInput] = useState('');

  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [ordersError, setOrdersError] = useState(null);

  // Track whether the bill has been printed for the selected order
  const [printedOrderId, setPrintedOrderId] = useState(null);
  const [printedAt, setPrintedAt] = useState(null);

  // Edit order functionality
  const [isEditingOrder, setIsEditingOrder] = useState(false);
  const [editedItems, setEditedItems] = useState([]);
  const [editRemarks, setEditRemarks] = useState('');

  useEffect(() => {
    fetchOrders();
    // optional: poll for updates
    // const interval = setInterval(fetchOrders, 5000);
    // return () => clearInterval(interval);
  }, []);

  async function fetchOrders() {
    setLoadingOrders(true);
    setOrdersError(null);
    try {
      const data = await ordersService.getAll();
      // Filter orders that are ready for billing (ready_for_billing status means sent from waiter)
      const readyOrders = (Array.isArray(data) ? data : []).filter(
        // Only show orders that are ready for billing at the counter — completed orders remove themselves
        order => order.status === 'ready_for_billing'
      );
      setOrders(readyOrders);
    } catch (err) {
      setOrdersError(err.message || 'Failed to load orders');
    } finally {
      setLoadingOrders(false);
    }
  }

  // Refresh orders periodically
  useEffect(() => {
    const interval = setInterval(fetchOrders, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    navigate('/login');
  };

  const handleSelectOrder = (order) => {
    setSelectedOrder(order);
    setDiscount(parseFloat(order.discount || 0));
    setDiscountInput(order.discount ? order.discount.toString() : '');
    // Reset printed state when switching to another order
    setPrintedOrderId(null);
    setPrintedAt(null);
    // Reset edit mode
    setIsEditingOrder(false);
    setEditedItems([]);
    setEditRemarks('');
  };

  const handleApplyDiscount = async () => {
    if (!selectedOrder) return;
    const discountValue = parseFloat(discountInput);
    if (!isNaN(discountValue) && discountValue >= 0) {
      setDiscount(discountValue);
      
      // Update order with discount in backend
      try {
        const subtotal = calculateSubtotal();
        const gst = subtotal * 0.05;
        const newTotal = subtotal + gst - discountValue;
        
        const updatedOrder = await ordersService.update(selectedOrder.id, {
          discount: discountValue,
          total: newTotal
        });
        
        // Update selected order with new data
        setSelectedOrder(updatedOrder);
        
        // Refresh orders list
        await fetchOrders();
      } catch (error) {
        console.error('Error applying discount:', error);
        alert('Failed to apply discount. Please try again.');
      }
    } else {
      alert('Please enter a valid discount amount');
    }
  };

  const handlePayment = async (method) => {
    if (!selectedOrder) return;

    // Ensure bill was printed before accepting payment
    if (printedOrderId !== selectedOrder.id) {
      alert('Please print the bill before accepting payment.');
      return;
    }

    // Normalize method to enum values expected by the backend
    const methodKey = String(method || '').toLowerCase();

    try {
      // mark order as completed and set payment method on the server
      await ordersService.update(selectedOrder.id, { status: 'completed', paymentMethod: methodKey, completedAt: new Date().toISOString() });
      alert(`Payment of ₹${calculateTotal()} processed via ${method}`);
      // clear printed state and selected order
      setPrintedOrderId(null);
      setPrintedAt(null);
      setSelectedOrder(null);
      await fetchOrders();
    } catch (err) {
      alert(err.message || 'Payment failed');
    }
  };

  const calculateSubtotal = () => {
    if (!selectedOrder) return 0;
    
    // Use edited items if in edit mode
    const itemsToCalculate = isEditingOrder ? editedItems : (selectedOrder.items || []);
    
    // Handle both API format and local format
    if (itemsToCalculate && itemsToCalculate.length > 0) {
      return itemsToCalculate.reduce((sum, item) => {
        const price = parseFloat(item.price || 0);
        const quantity = parseInt(item.quantity || item.qty || 0);
        return sum + (price * quantity);
      }, 0);
    }
    return parseFloat(selectedOrder.subtotal || 0);
  };

  const calculateGST = () => {
    if (!selectedOrder) return 0;
    // Use stored GST if available, otherwise calculate
    if (selectedOrder.gst !== undefined) {
      return parseFloat(selectedOrder.gst || 0);
    }
    return calculateSubtotal() * 0.05;
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const gst = calculateGST();
    return subtotal + gst - discount;
  };

  // Handle print button click
  const handlePrintClick = async () => {
    if (!selectedOrder) {
      alert('Please select an order to print');
      return;
    }

    // Get bill data
    const billData = formatBillData(
      selectedOrder,
      discount,
      calculateSubtotal,
      calculateGST,
      calculateTotal
    );

    // Use the unified print function
    await printBill(billData);

    // Mark as printed for the selected order (used to enable payment)
    setPrintedOrderId(selectedOrder.id);
    setPrintedAt(new Date().toISOString());
  };

  // Handle PDF download
  const handleDownloadPDF = async () => {
    if (!selectedOrder) {
      alert('Please select an order to download');
      return;
    }
    const billData = formatBillData(
      selectedOrder,
      discount,
      calculateSubtotal,
      calculateGST,
      calculateTotal
    );
    await generatePDF(billData, `Bill-${selectedOrder.id}.pdf`);
  };

  // Edit order functionality
  const handleEditOrder = () => {
    if (!selectedOrder) return;
    setIsEditingOrder(true);
    setEditedItems([...(selectedOrder.items || [])]);
    setEditRemarks(selectedOrder.notes || '');
  };

  const handleCancelEdit = () => {
    setIsEditingOrder(false);
    setEditedItems([]);
    setEditRemarks('');
  };

  const handleUpdateItemQuantity = (itemIndex, change) => {
    setEditedItems(prevItems => 
      prevItems.map((item, index) => {
        if (index === itemIndex) {
          const newQuantity = (item.quantity || item.qty || 1) + change;
          if (newQuantity <= 0) return null; // Remove item if quantity becomes 0
          return { ...item, quantity: newQuantity };
        }
        return item;
      }).filter(Boolean)
    );
  };

  const handleSaveOrderChanges = async () => {
    if (!selectedOrder) return;

    try {
      // Calculate new totals
      const subtotal = editedItems.reduce((sum, item) => {
        const price = parseFloat(item.price || 0);
        const quantity = parseInt(item.quantity || item.qty || 1);
        return sum + (price * quantity);
      }, 0);
      
      const gst = subtotal * 0.05;
      const total = subtotal + gst - discount;

      // Update order with modified items and remarks
      await ordersService.update(selectedOrder.id, {
        items: editedItems.map(item => ({
          itemName: item.itemName || item.name,
          quantity: item.quantity || item.qty,
          price: item.price,
          specialInstructions: item.specialInstructions || item.notes || ''
        })),
        subtotal,
        gst,
        total,
        notes: editRemarks.trim() || null
      });

      // Update local state
      const updatedOrder = {
        ...selectedOrder,
        items: editedItems,
        subtotal,
        gst,
        total,
        notes: editRemarks.trim() || null
      };
      setSelectedOrder(updatedOrder);

      // Refresh orders list
      await fetchOrders();

      // Exit edit mode
      setIsEditingOrder(false);
      setEditedItems([]);
      setEditRemarks('');

      alert('Order updated successfully!');
    } catch (error) {
      console.error('Error updating order:', error);
      alert('Failed to update order. Please try again.');
    }
  };

  // Show orders that are ready for billing (ready_for_billing status means sent from waiter)
  const readyOrders = orders.filter((order) => order.status === 'ready_for_billing');

  return (
    <Container>
      <Header>
        <h1>Counter / Billing</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.lg }}>
          <span style={{ color: theme.colors.textSecondary }}>
            {loadingOrders ? 'Loading orders...' : `${readyOrders.length} orders ready for billing`}
          </span>
          <LogoutButton variant="secondary" size="sm" onClick={handleLogout}>
            <LogOut size={18} />
            Logout
          </LogoutButton>
        </div>
      </Header>

      <ContentGrid>
        <OrdersList>
          <OrdersHeader>
            <h2>Ready Orders</h2>
          </OrdersHeader>

          {loadingOrders ? (
            <EmptyState>
              <p>Loading orders...</p>
            </EmptyState>
          ) : ordersError ? (
            <EmptyState>
              <p>{ordersError}</p>
            </EmptyState>
          ) : readyOrders.length === 0 ? (
            <EmptyState>
              <p>No orders ready for billing</p>
            </EmptyState>
          ) : (
            readyOrders.map((order) => (
              <OrderCard
                key={order.id}
                selected={selectedOrder?.id === order.id}
                onClick={() => handleSelectOrder(order)}
              >
                <OrderHeader>
                  <div>
                    <h3>{order.section} {order.tableNumber}</h3>
                    <p>
                      {order.waiterName ? `Waiter: ${order.waiterName} • ` : ''}
                      {order.orderNumber || order.id}
                      {order.createdAt ? ` • ${new Date(order.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : ''}
                    </p>
                  </div>
                  <span>₹{parseFloat(order.total || 0).toFixed(2)}</span>
                </OrderHeader>
                <OrderItems>
                  {(order.items || []).map((item, index) => (
                    <OrderItem key={index}>
                      <span>
                        {item.quantity || item.qty || 0}x {item.itemName || item.name || 'Item'}
                      </span>
                      <span>₹{((parseFloat(item.price || 0)) * (item.quantity || item.qty || 1)).toFixed(2)}</span>
                    </OrderItem>
                  ))}
                </OrderItems>
              </OrderCard>
            ))
          )}
        </OrdersList>

        <BillPanel>
          <BillHeader>
            <Receipt size={24} />
            <h2>{isEditingOrder ? 'Edit Order' : 'Bill Summary'}</h2>
            {selectedOrder && !isEditingOrder && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleEditOrder}
                style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: theme.spacing.xs }}
              >
                <Edit size={16} />
                Edit Order
              </Button>
            )}
            {selectedOrder && printedOrderId === selectedOrder.id && (
              <div style={{ marginLeft: 'auto', color: theme.colors.textSecondary, fontSize: theme.typography.fontSize.sm }}>
                Printed {printedAt ? new Date(printedAt).toLocaleTimeString() : ''}
              </div>
            )}
          </BillHeader>

          {selectedOrder ? (
            <>
              <BillDetails>
                <BillRow>
                  <span>Table: {selectedOrder.section} {selectedOrder.tableNumber}</span>
                  <span>{selectedOrder.waiterName ? `Waiter: ${selectedOrder.waiterName}` : selectedOrder.orderNumber}</span>
                </BillRow>

                <div style={{ marginTop: theme.spacing.lg, marginBottom: theme.spacing.lg }}>
                  {isEditingOrder ? (
                    // Edit mode: show editable items
                    editedItems.map((item, index) => {
                      const itemName = item.itemName || item.name || 'Item';
                      const quantity = item.quantity || item.qty || 1;
                      const price = parseFloat(item.price || 0);
                      const itemTotal = price * quantity;
                      return (
                        <div key={index} style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          marginBottom: theme.spacing.sm,
                          padding: theme.spacing.sm,
                          border: '1px solid #e0e0e0',
                          borderRadius: theme.borderRadius.md
                        }}>
                          <span style={{ flex: 1 }}>{itemName} - ₹{price.toFixed(2)}</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                            <Button 
                              variant="outline" 
                              size="xs" 
                              onClick={() => handleUpdateItemQuantity(index, -1)}
                              disabled={quantity <= 1}
                            >
                              -
                            </Button>
                            <span style={{ minWidth: '30px', textAlign: 'center' }}>{quantity}</span>
                            <Button 
                              variant="outline" 
                              size="xs" 
                              onClick={() => handleUpdateItemQuantity(index, 1)}
                            >
                              +
                            </Button>
                            <Button 
                              variant="danger" 
                              size="xs" 
                              onClick={() => handleUpdateItemQuantity(index, -quantity)}
                            >
                              Remove
                            </Button>
                          </div>
                          <span style={{ marginLeft: theme.spacing.sm }}>₹{itemTotal.toFixed(2)}</span>
                        </div>
                      );
                    })
                  ) : (
                    // Normal mode: show read-only items
                    (selectedOrder.items || []).map((item, index) => {
                      const itemName = item.itemName || item.name || 'Item';
                      const quantity = item.quantity || item.qty || 0;
                      const price = parseFloat(item.price || 0);
                      const itemTotal = price * quantity;
                      return (
                        <BillRow key={index}>
                          <span>{quantity}x {itemName}</span>
                          <span>₹{itemTotal.toFixed(2)}</span>
                        </BillRow>
                      );
                    })
                  )}
                </div>

                {isEditingOrder && (
                  <div style={{ marginBottom: theme.spacing.lg }}>
                    <label style={{ 
                      display: 'block', 
                      fontSize: theme.typography.fontSize.sm, 
                      fontWeight: theme.typography.fontWeight.semiBold,
                      marginBottom: theme.spacing.sm 
                    }}>
                      Remarks/Issue Description
                    </label>
                    <Input
                      as="textarea"
                      rows={3}
                      placeholder="Describe the issue with the order..."
                      value={editRemarks}
                      onChange={(e) => setEditRemarks(e.target.value)}
                      style={{ width: '100%', resize: 'vertical' }}
                    />
                  </div>
                )}

                <BillRow>
                  <span>Subtotal:</span>
                  <span>₹{calculateSubtotal().toFixed(2)}</span>
                </BillRow>
                <BillRow>
                  <span>GST (5%):</span>
                  <span>₹{calculateGST().toFixed(2)}</span>
                </BillRow>

                <DiscountSection>
                  <label style={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.semiBold }}>
                    Discount
                  </label>
                  <DiscountInput>
                    <Input
                      type="number"
                      placeholder="Enter discount"
                      value={discountInput}
                      onChange={(e) => setDiscountInput(e.target.value)}
                    />
                    <Button variant="outline" size="sm" onClick={handleApplyDiscount}>
                      Apply
                    </Button>
                  </DiscountInput>
                  {discount > 0 && (
                    <BillRow style={{ marginTop: theme.spacing.sm }}>
                      <span>Discount:</span>
                      <span>-₹{discount.toFixed(2)}</span>
                    </BillRow>
                  )}
                </DiscountSection>

                <BillRow highlight>
                  <span>Total:</span>
                  <span>₹{calculateTotal().toFixed(2)}</span>
                </BillRow>
              </BillDetails>

              <ActionsContainer>
                {isEditingOrder ? (
                  // Edit mode buttons
                  <div style={{ display: 'flex', gap: theme.spacing.md, flexDirection: 'column' }}>
                    <div style={{ display: 'flex', gap: theme.spacing.sm }}>
                      <Button 
                        variant="primary" 
                        onClick={handleSaveOrderChanges}
                        style={{ flex: 1 }}
                      >
                        Save Changes
                      </Button>
                      <Button 
                        variant="secondary" 
                        onClick={handleCancelEdit}
                      >
                        Cancel
                      </Button>
                    </div>
                    <div style={{ color: theme.colors.textSecondary, fontSize: theme.typography.fontSize.sm }}>
                      Use +/- buttons to adjust quantities. Items with 0 quantity will be removed.
                    </div>
                  </div>
                ) : (
                  // Normal mode buttons
                  <>
                    <PaymentButtons>
                    <PaymentButton variant="primary" disabled={!(selectedOrder && printedOrderId === selectedOrder.id)} onClick={() => handlePayment('Cash')}>
                      <DollarSign />
                      <span>Cash</span>
                    </PaymentButton>
                    <PaymentButton variant="accent" disabled={!(selectedOrder && printedOrderId === selectedOrder.id)} onClick={() => handlePayment('UPI')}>
                      <Smartphone />
                      <span>UPI</span>
                    </PaymentButton>
                    <PaymentButton variant="primary" disabled={!(selectedOrder && printedOrderId === selectedOrder.id)} onClick={() => handlePayment('Card')}>
                      <CreditCard />
                      <span>Card</span>
                    </PaymentButton>
                    <PaymentButton variant="secondary" disabled={!(selectedOrder && printedOrderId === selectedOrder.id)} onClick={() => handlePayment('Other')}>
                      <MoreHorizontal />
                      <span>Other</span>
                    </PaymentButton>
                    </PaymentButtons>

                    <ActionButtons>
                      <ActionButton onClick={handlePrintClick}>
                        <Printer />
                        <span>Print</span>
                      </ActionButton>
                      <ActionButton onClick={handleDownloadPDF}>
                        <Download />
                        <span>PDF</span>
                      </ActionButton>
                    </ActionButtons>

                    {/* Show a small reminder if payment is not yet enabled */}
                    {selectedOrder && printedOrderId !== selectedOrder.id && (
                      <div style={{ color: theme.colors.textSecondary, fontSize: theme.typography.fontSize.sm, marginTop: theme.spacing.sm }}>
                        Please print the bill before accepting payment.
                      </div>
                    )}
                  </>
                )}
              </ActionsContainer>
            </>
          ) : (
            <EmptyState>
              <p>Select an order to view bill</p>
            </EmptyState>
          )}
        </BillPanel>
      </ContentGrid>

      <Footer hasSidebar={false} />
    </Container>
  );
}

export default CounterBilling;

